import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, CheckCheck, Trash2, Archive, ArchiveRestore, EyeOff, Eye,
  Settings, MoreHorizontal, Inbox, MessageSquare, UserPlus, Calendar,
  ClipboardList, LifeBuoy, ShieldAlert, Star, Sparkles, Megaphone, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL__;

// Messenger has its own notification surfaces, so message-family records
// must not appear on the global Notifications page for normal users.
// Admin system notifications are loaded from the admin endpoint and are
// not filtered through this user-facing Messenger filter.
const MESSENGER_TYPES = new Set([
  'new_message', 'message', 'new_conversation_request',
  'new_voice_note', 'nudge', 'voice_note', 'message_request',
  'messages', 'direct_message', 'direct_messages', 'dm', 'dms',
  'messenger', 'chat', 'chats', 'conversation', 'conversations',
  'group_message', 'group_messages', 'group_chat', 'group_chats',
  'voice_notes', 'nudges', 'conversation_request', 'conversation_requests',
]);

const normaliseKey = (value) => String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');

const isMessengerNotif = (n) => {
  if (!n) return false;

  const metadata = n.metadata || {};
  const keysToCheck = [
    n.category,
    n.type,
    n.event_type,
    n.notification_type,
    n.kind,
    n.source,
    metadata.category,
    metadata.type,
    metadata.event_type,
    metadata.notification_type,
    metadata.kind,
    metadata.source,
  ]
    .map(normaliseKey)
    .filter(Boolean);

  if (keysToCheck.some((key) => MESSENGER_TYPES.has(key))) return true;

  if (n.message_id || n.conversation_id) return true;
  if (metadata.message_id || metadata.conversation_id || metadata.thread_id || metadata.chat_id) return true;

  const link = normaliseKey(n.link || n.url || n.action_url || n.target_url || n.destination_url);
  if (link.startsWith('/messages') || link.includes('/messages/')) return true;

  return keysToCheck.some((key) => (
    key.includes('message')
    || key.includes('messenger')
    || key.includes('conversation')
    || key.includes('nudge')
    || key.includes('voice_note')
  ));
};

const isUnread = (n) => n.read === false || n.is_read === false;
const isArchived = (n) => Boolean(n.archived || n.is_archived);
const getNotificationId = (n) => n?.id || n?._id;

const isAdminBroadcastNotification = (n) => {
  if (!n) return false;
  const metadata = n.metadata || {};
  const keys = [
    n.type,
    n.event_type,
    n.notification_type,
    metadata.type,
    metadata.event_type,
    metadata.source,
  ].map(normaliseKey).filter(Boolean);

  return Boolean(
    metadata.admin_broadcast === true
    || metadata.overlay === true
    || metadata.show_overlay === true
    || keys.includes('admin_broadcast')
    || keys.includes('maintenance_notice')
    || keys.includes('promotion_broadcast')
  );
};

const getAdminBroadcastActionUrl = (n) => {
  if (!n) return null;
  const metadata = n.metadata || {};
  const value = metadata.action_url
    || metadata.link
    || metadata.url
    || n.action_url
    || n.target_url;
  const text = String(value || '').trim();

  // Do not treat the default /notifications fallback as a real CTA link.
  // Rows can still reopen the popup, but the popup only shows Open when
  // an admin supplied a genuine action URL.
  if (!text || text === '/notifications') return null;
  return text;
};

// Generate a human-readable message from admin notification event_type + metadata.
// This mirrors the working NotificationsDropdown behaviour.
function getAdminNotifMessage(notif) {
  const { event_type, metadata } = notif || {};

  if (event_type === 'user_registered') return `New user registered: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_archived') return `${metadata?.count || 1} user(s) archived`;
  if (event_type === 'user_unarchived') return `User unarchived: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_bulk_deleted') return `${metadata?.count || 1} user(s) deleted`;
  if (event_type === 'user_upgraded') return `User upgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_downgraded') return `User downgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'payment_failed') return `Payment failed for ${metadata?.email || 'a user'}`;
  if (event_type === 'inactivity_archive_run') return `Inactivity archive: ${metadata?.count || 0} users archived`;

  return event_type?.replace(/_/g, ' ') || 'New notification';
}

function normaliseNotificationsPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.notifications)) return data.notifications;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function normaliseNotificationRecord(notification, isAdmin = false) {
  if (!notification) return notification;

  return {
    ...notification,
    id: notification.id || notification._id,
    created_at: notification.created_at || notification.timestamp,
    message: isAdmin
      ? notification.message || getAdminNotifMessage(notification)
      : notification.message,
  };
}

// ─── Type → icon + accent colour mapping ──────────────────────────────
// Visual hierarchy upgrade vs the old plain rows. Single map drives both
// the dropdown and the full page so the visual language stays consistent.
const TYPE_VISUALS = {
  connection_request:  { Icon: UserPlus,    tone: 'text-blue-500',   bg: 'bg-blue-500/10' },
  connection_accepted: { Icon: UserPlus,    tone: 'text-emerald-500',bg: 'bg-emerald-500/10' },
  roster_invitation:   { Icon: UserPlus,    tone: 'text-blue-500',   bg: 'bg-blue-500/10' },
  roster_accepted:     { Icon: UserPlus,    tone: 'text-emerald-500',bg: 'bg-emerald-500/10' },
  project_invitation:  { Icon: Sparkles,    tone: 'text-amber-500',  bg: 'bg-amber-500/10' },
  planner_reminder:    { Icon: ClipboardList,tone: 'text-amber-500', bg: 'bg-amber-500/10' },
  event_unpublished:   { Icon: Calendar,    tone: 'text-rose-500',   bg: 'bg-rose-500/10' },
  support_issue_received:     { Icon: LifeBuoy, tone: 'text-blue-500', bg: 'bg-blue-500/10' },
  support_issue_acknowledged: { Icon: LifeBuoy, tone: 'text-blue-500', bg: 'bg-blue-500/10' },
  support_issue_reply:        { Icon: LifeBuoy, tone: 'text-blue-500', bg: 'bg-blue-500/10' },
  support_issue_resolved:     { Icon: LifeBuoy, tone: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  account_suspended:   { Icon: ShieldAlert, tone: 'text-rose-500',   bg: 'bg-rose-500/10' },
  account_unsuspended: { Icon: ShieldAlert, tone: 'text-emerald-500',bg: 'bg-emerald-500/10' },
  lifetime_free_granted: { Icon: Star,      tone: 'text-amber-500',  bg: 'bg-amber-500/10' },
  new_message:         { Icon: MessageSquare, tone: 'text-blue-500', bg: 'bg-blue-500/10' },
};

const getVisual = (type) => TYPE_VISUALS[type] || { Icon: Bell, tone: 'text-theme-primary', bg: 'bg-theme-primary/10' };

// ─── Date grouping ─────────────────────────────────────────────────────
const startOfToday = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime();
};
const startOfYesterday = () => startOfToday() - 86400000;
const startOfThisWeek = () => startOfToday() - 6 * 86400000;

const bucketFor = (createdAt) => {
  if (!createdAt) return 'earlier';
  const t = new Date(createdAt).getTime();
  if (isNaN(t)) return 'earlier';
  if (t >= startOfToday()) return 'today';
  if (t >= startOfYesterday()) return 'yesterday';
  if (t >= startOfThisWeek()) return 'this_week';
  return 'earlier';
};

const BUCKET_ORDER = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'earlier',   label: 'Earlier' },
];

const formatTime = (createdAt) => {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// ─── Main page ─────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.active_role === 'admin' || user?.role === 'admin';

  const [tab, setTab] = useState('all'); // 'all' | 'unread' | 'archived'
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [overlayNotification, setOverlayNotification] = useState(null);

  // Clear selection when the tab changes so a "Delete selected" button
  // can never touch rows from a different tab than the user is viewing.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab]);

  useEffect(() => {
    // Admin now supports the Archived tab as well (M4 parity fix). Kept
    // as a no-op effect for potential future gating.
  }, [isAdmin, tab]);

  const load = useCallback(async (which = tab, { silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      if (isAdmin) {
        const params = which === 'archived'
          ? { limit: 100, include_archived: true }
          : { limit: 100 };

        const { data } = await axios.get(`${API_URL}/api/admin/notifications`, {
          params,
          withCredentials: true,
        });

        const adminNotifications = normaliseNotificationsPayload(data)
          .map((n) => normaliseNotificationRecord(n, true))
          .filter((n) => getNotificationId(n));

        setNotifications(adminNotifications);
        return;
      }

      const params = which === 'archived'
        ? { include_archived: true }
        : {};

      const { data } = await axios.get(`${API_URL}/api/notifications`, {
        params,
        withCredentials: true,
      });

      const all = normaliseNotificationsPayload(data)
        .map((n) => normaliseNotificationRecord(n, false))
        .filter((n) => getNotificationId(n))
        .filter((n) => !isMessengerNotif(n));

      setNotifications(all);
    } catch (error) {
      console.error('Failed to load notifications page', error);

      if (!silent) {
        setNotifications([]);
        toast.error('Could not load notifications');
      }

      return false;
    } finally {
      if (!silent) setLoading(false);
    }

    return true;
  }, [tab, isAdmin]);

  useEffect(() => {
    // Fetch on tab change or auth change. Defer through a microtask so
    // React Hooks lint doesn't classify the setState calls inside `load`
    // as "set state during effect" — these are real async-fetch updates
    // that happen well after the effect completes.
    let alive = true;
    Promise.resolve().then(() => { if (alive) load(); });
    return () => { alive = false; };
  }, [load, user?.id]);

  // Filtered view for the current tab
  const visible = useMemo(() => {
    if (isAdmin) {
      if (tab === 'unread') return notifications.filter((n) => isUnread(n) && !isArchived(n));
      if (tab === 'archived') return notifications.filter((n) => isArchived(n));
      return notifications.filter((n) => !isArchived(n));
    }

    if (tab === 'unread') return notifications.filter((n) => isUnread(n) && !isArchived(n));
    if (tab === 'archived') return notifications.filter((n) => isArchived(n));
    return notifications.filter((n) => !isArchived(n));
  }, [notifications, tab, isAdmin]);

  const grouped = useMemo(() => {
    const map = { today: [], yesterday: [], this_week: [], earlier: [] };
    visible.forEach((n) => { map[bucketFor(n.created_at)].push(n); });
    return map;
  }, [visible]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => isUnread(n) && !isArchived(n)).length,
    [notifications],
  );

  const totalArchived = useMemo(
    () => notifications.filter((n) => isArchived(n)).length,
    [notifications],
  );

  const tabOptions = useMemo(() => {
    const options = [
      { key: 'all', label: 'All', count: notifications.filter((n) => !isArchived(n)).length },
      { key: 'unread', label: 'Unread', count: unreadCount },
      { key: 'archived', label: 'Archived', count: totalArchived },
    ];

    return options;
  }, [notifications, unreadCount, totalArchived]);

  // ─── Actions ──────────────────────────────────────────────────────
  const optimisticUpdate = (id, patch) => {
    setNotifications((prev) => prev.map((n) => (getNotificationId(n) === id ? { ...n, ...patch } : n)));
  };

  const optimisticRemove = (id) => {
    setNotifications((prev) => prev.filter((n) => getNotificationId(n) !== id));
  };

  const notifyChanged = () => {
    window.dispatchEvent(new Event('notifications:changed'));
    if (isAdmin) {
      window.dispatchEvent(new Event('admin:notifications-changed'));
      window.dispatchEvent(new Event('admin:stats-refresh'));
    }
  };

  const markRead = async (id) => {
    optimisticUpdate(id, { read: true, is_read: true });

    try {
      if (isAdmin) {
        await axios.post(
          `${API_URL}/api/admin/notifications/mark-read`,
          { notification_ids: [id] },
          { withCredentials: true },
        );
      } else {
        await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
      }

      notifyChanged();
    } catch {
      toast.error('Failed to mark as read');
      load(tab);
    }
  };

  const markUnread = async (id) => {
    optimisticUpdate(id, { read: false, is_read: false });

    try {
      if (isAdmin) {
        await axios.put(`${API_URL}/api/admin/notifications/${id}/unread`, {}, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/notifications/${id}/unread`, {}, { withCredentials: true });
      }
      notifyChanged();
    } catch {
      toast.error('Could not mark as unread. Please try again.');
      load(tab);
    }
  };

  const archive = async (id) => {
    optimisticUpdate(id, { archived: true, is_archived: true });

    try {
      if (isAdmin) {
        await axios.post(`${API_URL}/api/admin/notifications/${id}/archive`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/notifications/${id}/archive`, {}, { withCredentials: true });
      }
      await load(tab, { silent: true });
      toast.success('Notification archived');
      notifyChanged();
    } catch {
      toast.error('Could not archive. Please try again.');
      load(tab);
    }
  };

  const unarchive = async (id) => {
    optimisticUpdate(id, { archived: false, is_archived: false });

    try {
      if (isAdmin) {
        await axios.post(`${API_URL}/api/admin/notifications/${id}/unarchive`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/notifications/${id}/unarchive`, {}, { withCredentials: true });
      }
      await load(tab, { silent: true });
      toast.success('Restored');
      notifyChanged();
    } catch {
      toast.error('Could not restore. Please try again.');
      load(tab);
    }
  };

  const deleteOne = async (id) => {
    optimisticRemove(id);
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

    try {
      if (isAdmin) {
        await axios.delete(`${API_URL}/api/admin/notifications/${id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API_URL}/api/notifications/${id}`, { withCredentials: true });
      }
      notifyChanged();
    } catch {
      toast.error('Could not remove. Please try again.');
      load(tab);
    }
  };

  // ─── Selection helpers ───────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleIds = useMemo(
    () => visible.map((n) => getNotificationId(n)).filter(Boolean),
    [visible],
  );

  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        // Deselect only the ones currently visible; preserve any selection
        // outside the current filtered view (defensive — the effect on
        // `tab` above already clears selection, but be explicit).
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkArchive = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);

    // Optimistic: remove archived rows from the current (non-archived) view.
    setNotifications((prev) => prev.map((n) => (
      ids.includes(getNotificationId(n))
        ? { ...n, archived: true, is_archived: true }
        : n
    )));

    try {
      const url = isAdmin
        ? `${API_URL}/api/admin/notifications/bulk-archive`
        : `${API_URL}/api/notifications/bulk-archive`;
      const { data } = await axios.post(url, { notification_ids: ids }, { withCredentials: true });
      const affected = data?.archived ?? ids.length;
      await load(tab, { silent: true });
      toast.success(
        affected === 1 ? '1 notification archived' : `${affected} notifications archived`,
      );
      clearSelection();
      notifyChanged();
    } catch {
      toast.error('Could not archive selected. Please try again.');
      load(tab);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);

    // Optimistic remove.
    setNotifications((prev) => prev.filter((n) => !ids.includes(getNotificationId(n))));

    try {
      const url = isAdmin
        ? `${API_URL}/api/admin/notifications/bulk-delete`
        : `${API_URL}/api/notifications/bulk-delete`;
      const { data } = await axios.post(url, { notification_ids: ids }, { withCredentials: true });
      const affected = data?.deleted ?? ids.length;
      toast.success(
        affected === 1 ? '1 notification deleted' : `${affected} notifications deleted`,
      );
      clearSelection();
      notifyChanged();
    } catch {
      toast.error('Could not delete selected. Please try again.');
      load(tab);
    } finally {
      setBulkBusy(false);
    }
  };

  const clearAllAdminNotifications = async () => {
    if (!isAdmin || clearingAll) return;
    const confirmed = window.confirm(
      'Clear all your admin notifications? This only clears your own admin feed and cannot be undone.',
    );
    if (!confirmed) return;

    setClearingAll(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/admin/notifications?confirm=true`,
        { withCredentials: true },
      );
      setNotifications([]);
      clearSelection();
      toast.success(
        data?.deleted === 1
          ? '1 admin notification cleared'
          : `${data?.deleted || 0} admin notifications cleared`,
      );
      notifyChanged();
    } catch {
      toast.error('Could not clear admin notifications. Please try again.');
      load(tab);
    } finally {
      setClearingAll(false);
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);

    try {
      if (isAdmin) {
        await axios.post(`${API_URL}/api/admin/notifications/mark-all-read`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      }

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true, is_read: true })));
      toast.success('All notifications marked as read');
      notifyChanged();
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const openDestination = (n) => {
    const id = getNotificationId(n);

    // Always mark read on open.
    if (id && isUnread(n)) markRead(id);

    if (!isAdmin && isAdminBroadcastNotification(n)) {
      setOverlayNotification(n);
      return;
    }

    const destination = n.destination_url
      || n.metadata?.action_url
      || n.link
      || n.url
      || n.action_url
      || n.target_url;

    if (destination) navigate(destination);
  };

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <>
      <main className="min-h-screen bg-theme pt-6 sm:pt-8 lg:pt-8 pb-16" data-testid="notifications-page">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Notification hub header */}
        <section
          className="mb-6 overflow-hidden rounded-2xl border border-theme bg-theme-secondary shadow-sm"
          data-testid="notifications-hub-header"
        >
          <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-theme-primary/10">
                <Bell className="h-5 w-5 text-theme-primary" />
              </div>

              <div className="min-w-0">
                <h1 className="text-2xl font-bold leading-tight text-theme-text sm:text-3xl">
                  Notifications
                </h1>
                <p className="mt-0.5 text-xs text-theme-muted sm:text-sm">
                  {unreadCount > 0
                    ? `You have ${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}.`
                    : 'You are all caught up.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {isAdmin && notifications.length > 0 && (
                <Button
                  onClick={clearAllAdminNotifications}
                  disabled={clearingAll}
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:border-red-500 hover:bg-red-500 hover:text-white"
                  data-testid="notifications-admin-clear-all"
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Clear all
                </Button>
              )}

              <Button
                onClick={markAllRead}
                disabled={markingAll || unreadCount === 0}
                variant="outline"
                size="sm"
                className="border-theme text-theme-text hover:border-theme-primary hover:bg-theme-primary hover:text-white"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck className="mr-1.5 h-4 w-4" />
                Mark all read
              </Button>

              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-theme-muted hover:text-theme-text"
              >
                <Link
                  to="/dashboard?tab=settings"
                  data-testid="notifications-settings-link"
                >
                  <Settings className="mr-1.5 h-4 w-4" />
                  Settings
                </Link>
              </Button>
            </div>
          </div>

          <div className="border-t border-theme px-3 py-3 sm:px-5">
            <div className="inline-flex w-full items-center gap-1 rounded-xl bg-theme-hover p-1 sm:w-auto">
              {tabOptions.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`notifications-tab-${t.key}`}
                  className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-all sm:flex-none ${
                    tab === t.key
                      ? 'bg-theme-surface text-theme-primary shadow-sm'
                      : 'text-theme-muted hover:bg-theme-surface/60 hover:text-theme-text'
                  }`}
                >
                  <span>{t.label}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      tab === t.key
                        ? 'bg-theme-primary/10 text-theme-primary'
                        : 'bg-theme-secondary text-theme-muted'
                    }`}
                  >
                    {t.count ?? 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 xl:gap-8 items-start">

          {/* Main feed column */}
          <div className="min-w-0">

            {/* Content */}
            {loading ? (
              <NotifSkeleton />
            ) : visible.length === 0 ? (
              <EmptyState tab={tab} isAdmin={isAdmin} />
            ) : (
              <>
                {/* Bulk selection toolbar — visible only when the list has rows */}
                <div
                  className="flex items-center gap-3 mb-4 px-1"
                  data-testid="notifications-bulk-toolbar"
                >
                  <label
                    className="flex items-center gap-2 text-xs font-medium text-theme-muted cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-theme text-theme-primary focus:ring-theme-primary cursor-pointer"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all visible notifications"
                      data-testid="notifications-select-all"
                    />
                    {allVisibleSelected ? 'Deselect all' : 'Select all'}
                  </label>

                  {selectedIds.size > 0 && (
                    <div
                      className="flex items-center gap-2 ml-auto"
                      data-testid="notifications-bulk-actions"
                    >
                      <span
                        className="text-xs font-semibold text-theme-text"
                        data-testid="notifications-selected-count"
                      >
                        {selectedIds.size} selected
                      </span>
                      {tab !== 'archived' && (
                        <Button
                          onClick={bulkArchive}
                          disabled={bulkBusy}
                          variant="outline"
                          size="sm"
                          className="border-theme text-theme-text hover:bg-theme-primary hover:text-white hover:border-theme-primary"
                          data-testid="notifications-bulk-archive-btn"
                        >
                          <Archive className="h-4 w-4 mr-1.5" />
                          Archive selected
                        </Button>
                      )}
                      <Button
                        onClick={bulkDelete}
                        disabled={bulkBusy}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500"
                        data-testid="notifications-bulk-delete-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Delete selected
                      </Button>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="p-1.5 rounded-lg text-theme-muted hover:bg-theme-hover hover:text-theme-text"
                        aria-label="Clear selection"
                        data-testid="notifications-clear-selection"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  {BUCKET_ORDER.map(({ key, label }) => {
                    const items = grouped[key];
                    if (!items.length) return null;
                    return (
                      <section key={key} data-testid={`notifications-group-${key}`}>
                        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-theme-muted mb-2 px-1">{label}</h2>
                        <div className="bg-theme-secondary border border-theme rounded-2xl divide-y divide-theme overflow-hidden">
                          {items.map((n) => {
                            const id = getNotificationId(n);

                            return (
                              <NotifRow
                                key={id}
                                n={n}
                                tab={tab}
                                isAdmin={isAdmin}
                                selected={selectedIds.has(id)}
                                onToggleSelect={() => toggleSelect(id)}
                                onOpen={() => openDestination(n)}
                                onMarkRead={() => markRead(id)}
                                onMarkUnread={() => markUnread(id)}
                                onArchive={() => archive(id)}
                                onUnarchive={() => unarchive(id)}
                                onDelete={() => deleteOne(id)}
                              />
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Desktop support rail */}
          <aside className="hidden xl:flex xl:flex-col gap-4 sticky top-24" data-testid="notifications-support-rail">
            <SupportPanel
              unreadCount={unreadCount}
              totalArchived={totalArchived}
              isAdmin={isAdmin}
            />
          </aside>

        </div>
      </div>
      </main>

      {overlayNotification && !isAdmin && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center px-4 py-6" role="dialog"
      data-ccuk-mobile-dialog-root="true" aria-modal="true" data-testid="notifications-admin-broadcast-overlay">
          <button
            type="button"
            className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
            onClick={() => setOverlayNotification(null)}
            aria-label="Dismiss notification overlay backdrop"
          />
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-theme bg-theme-surface shadow-2xl">
            <div className="absolute inset-x-0 top-0 h-1 bg-[#002FA7]" />
            <button
              type="button"
              onClick={() => setOverlayNotification(null)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-theme-muted hover:bg-theme-secondary hover:text-theme-text"
              aria-label="Dismiss notification"
              data-testid="notifications-admin-broadcast-overlay-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="p-6 pr-12">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#002FA7]/10 text-[#002FA7]">
                <Megaphone className="h-6 w-6" />
              </div>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#002FA7]">Casting Calls UK update</p>
              <h2 className="text-xl font-bold text-theme-text" data-testid="notifications-admin-broadcast-overlay-title">
                {overlayNotification.title || overlayNotification.metadata?.title || 'Platform update'}
              </h2>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-theme-muted" data-testid="notifications-admin-broadcast-overlay-message">
                {overlayNotification.message}
              </p>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOverlayNotification(null)}
                  className="border-theme text-theme-text"
                  data-testid="notifications-admin-broadcast-overlay-dismiss"
                >
                  Dismiss
                </Button>
                {getAdminBroadcastActionUrl(overlayNotification) && (
                  <Button
                    type="button"
                    onClick={() => {
                      const dest = getAdminBroadcastActionUrl(overlayNotification);
                      setOverlayNotification(null);
                      if (dest) navigate(dest);
                    }}
                    className="bg-[#002FA7] text-white hover:bg-[#0055FF]"
                    data-testid="notifications-admin-broadcast-overlay-open"
                  >
                    Open
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────
function NotifRow({
  n,
  tab,
  isAdmin,
  selected,
  onToggleSelect,
  onOpen,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onUnarchive,
  onDelete,
}) {
  const { Icon, tone, bg } = getVisual(n.type || n.event_type);
  const unread = isUnread(n);
  const id = getNotificationId(n);
  const title = n.title || (n.message ? n.message.split('. ')[0] : 'Notification');
  const body = n.message && n.title && n.message !== n.title ? n.message : null;
  const destination = n.destination_url
    || n.metadata?.action_url
    || n.link
    || n.url
    || n.action_url
    || n.target_url;
  const canOpen = Boolean(destination) || (!isAdmin && isAdminBroadcastNotification(n));

  // M4 parity: admins now have the same full lifecycle actions as normal
  // users — always show the kebab menu.
  const showMenu = true;

  return (
    <div
      className={`group relative flex items-start gap-3 p-4 transition-colors ${
        unread ? 'bg-theme-primary/5 hover:bg-theme-primary/10' : 'hover:bg-theme-hover'
      } ${selected ? 'bg-theme-primary/10' : ''} ${
        canOpen ? 'cursor-pointer' : 'cursor-default'
      }`}
      onClick={canOpen ? onOpen : undefined}
      data-testid={`notification-row-${id}`}
    >
      {/* Selection checkbox */}
      <div className="pt-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggleSelect}
          className="h-4 w-4 rounded border-theme text-theme-primary focus:ring-theme-primary cursor-pointer"
          aria-label="Select notification"
          data-testid={`notification-checkbox-${id}`}
        />
      </div>

      {/* Unread dot */}
      <div className="w-2 h-2 mt-2 shrink-0">
        {unread && <span className="block w-2 h-2 rounded-full bg-theme-primary" />}
      </div>

      {/* Type icon */}
      <div className={`h-10 w-10 shrink-0 rounded-full ${bg} flex items-center justify-center`}>
        <Icon className={`h-4.5 w-4.5 ${tone}`} style={{ height: 18, width: 18 }} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className={`text-sm leading-snug ${unread ? 'font-semibold text-theme-text' : 'font-medium text-theme-text'}`}>
            {title}
          </h3>
          <time className="text-[11px] text-theme-muted whitespace-nowrap shrink-0 pt-0.5">
            {formatTime(n.created_at)}
          </time>
        </div>
        {body && (
          <p className="text-xs text-theme-muted mt-1 leading-relaxed line-clamp-2">{body}</p>
        )}
      </div>

      {/* Kebab menu */}
      {showMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1.5 rounded-lg text-theme-muted hover:bg-theme-hover hover:text-theme-text"
              aria-label="Notification actions"
              data-testid={`notification-menu-${id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-theme-surface border-theme">
            {unread ? (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMarkRead(); }}>
                <Eye className="h-4 w-4 mr-2" /> Mark as read
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMarkUnread(); }}>
                <EyeOff className="h-4 w-4 mr-2" /> Mark as unread
              </DropdownMenuItem>
            )}

            {tab === 'archived' ? (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onUnarchive(); }}>
                <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onArchive(); }}>
                <Archive className="h-4 w-4 mr-2" /> Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-red-500 focus:text-red-500"
              data-testid={`notification-delete-${id}`}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// ─── Empty / loading ───────────────────────────────────────────────────
function EmptyState({ tab, isAdmin }) {
  const map = {
    all:      { Icon: Bell,    title: isAdmin ? 'No admin notifications yet' : 'No notifications yet', body: 'When something needs your attention, it will appear here.' },
    unread:   { Icon: CheckCheck, title: 'You are all caught up', body: 'No unread notifications.' },
    archived: { Icon: Inbox,   title: 'No archived notifications', body: 'Archived notifications will appear here.' },
  };

  const { Icon, title, body } = map[tab] || map.all;

  return (
    <div className="bg-theme-secondary border border-theme rounded-2xl py-16 px-6 text-center" data-testid={`notifications-empty-${tab}`}>
      <div className="h-16 w-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center mx-auto mb-4">
        <Icon className="h-7 w-7 text-theme-primary/70" />
      </div>
      <h2 className="text-lg font-semibold text-theme-text mb-1">{title}</h2>
      <p className="text-sm text-theme-muted max-w-sm mx-auto">{body}</p>
    </div>
  );
}

function NotifSkeleton() {
  return (
    <div className="bg-theme-secondary border border-theme rounded-2xl divide-y divide-theme">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-3 p-4 animate-pulse">
          <div className="w-2 h-2 mt-2" />
          <div className="h-10 w-10 rounded-full bg-theme-hover" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-2/3 bg-theme-hover rounded" />
            <div className="h-3 w-1/2 bg-theme-hover rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Desktop support rail ──────────────────────────────────────────────
function SupportPanel({ unreadCount, totalArchived, isAdmin }) {
  return (
    <>
      {/* Status summary */}
      <div className="bg-theme-secondary border border-theme rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="h-4 w-4 text-theme-primary" />
          <h3 className="text-sm font-semibold text-theme-text">Inbox at a glance</h3>
        </div>
        <dl className="space-y-2.5 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-theme-muted">Unread</dt>
            <dd className="text-theme-text font-semibold">{unreadCount}</dd>
          </div>
          {!isAdmin && (
            <div className="flex items-baseline justify-between">
              <dt className="text-theme-muted">Archived</dt>
              <dd className="text-theme-text font-semibold">{totalArchived}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Notification preferences shortcut */}
      <div className="bg-theme-secondary border border-theme rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-4 w-4 text-theme-primary" />
          <h3 className="text-sm font-semibold text-theme-text">Notification settings</h3>
        </div>
        <p className="text-xs text-theme-muted leading-relaxed mb-3">
          Manage what you get notified about — connections, messages, casting
          calls, planner reminders and account activity.
        </p>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="w-full border-theme text-theme-text hover:bg-theme-primary hover:text-white hover:border-theme-primary"
        >
          <Link to="/dashboard?tab=settings" data-testid="notifications-support-settings-link">
            Manage preferences
          </Link>
        </Button>
      </div>

      {/* Safety/account quick link */}
      <div className="bg-theme-secondary border border-theme rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="h-4 w-4 text-theme-primary" />
          <h3 className="text-sm font-semibold text-theme-text">Account &amp; safety</h3>
        </div>
        <p className="text-xs text-theme-muted leading-relaxed mb-3">
          Security alerts and account moderation notices appear here as soon
          as they happen.
        </p>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="w-full text-theme-primary hover:bg-theme-primary/10"
        >
          <Link to="/dashboard?tab=support" data-testid="notifications-support-help-link">
            Contact support
          </Link>
        </Button>
      </div>
    </>
  );
}
